import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LocationSummary, Me, Role } from '../api/types';
import { canSee, type PageKey } from '../lib/permissions';
import { useApi } from '../services';

const SITE_KEY = 'dp-site';

interface SessionValue {
  me: Me;
  role: Role;
  sites: LocationSummary[];
  site: LocationSummary;
  siteId: string;
  setSiteId: (id: string) => void;
  can: (page: PageKey) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

function readStoredSite(): string | null {
  try {
    return localStorage.getItem(SITE_KEY);
  } catch {
    return null;
  }
}

export function useMeQuery(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ['me'], queryFn: () => api.getMe(), enabled, staleTime: 5 * 60_000 });
}

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [stored, setStored] = useState<string | null>(readStoredSite);
  const site = me.locations.find((l) => l.id === stored) ?? me.locations[0];

  useEffect(() => {
    if (site && site.id !== stored) setStored(site.id);
  }, [site, stored]);

  const setSiteId = useCallback((id: string) => {
    setStored(id);
    try {
      localStorage.setItem(SITE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      role: me.user.role,
      sites: me.locations,
      site,
      siteId: site?.id,
      setSiteId,
      can: (page: PageKey) => canSee(me.user.role, page),
    }),
    [me, site, setSiteId],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}
